import { MFATypes } from '@flashbacktech/flashbackclient/api';

const BASE64_PADDING = '=';

const ensureBase64Padding = (value: string): string => {
  const missing = (4 - (value.length % 4)) % 4;
  return value + BASE64_PADDING.repeat(missing);
};

const base64UrlToBase64 = (value: string): string => {
  return ensureBase64Padding(value.replace(/-/g, '+').replace(/_/g, '/'));
};

const base64ToBase64Url = (value: string): string => {
  return value.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const uint8ArrayToArrayBuffer = (bytes: Uint8Array): ArrayBuffer => {
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return buffer;
};

export const base64UrlToArrayBuffer = (value: string): ArrayBuffer => {
  const binary = atob(base64UrlToBase64(value));
  return uint8ArrayToArrayBuffer(Uint8Array.from(binary, (char: string) => char.charCodeAt(0)));
};

export const arrayBufferToBase64Url = (value: ArrayBuffer): string => {
  const binary = String.fromCharCode(...new Uint8Array(value));
  return base64ToBase64Url(btoa(binary));
};

export const stringToArrayBuffer = (value: string): ArrayBuffer => {
  return uint8ArrayToArrayBuffer(new TextEncoder().encode(value));
};

export const toPublicKeyCredentialRequestOptions = (
  options: MFATypes.PasskeyAuthenticationOptions
): PublicKeyCredentialRequestOptions => {
  return {
    challenge: base64UrlToArrayBuffer(options.challenge),
    rpId: options.rpId,
    timeout: options.timeout,
    userVerification: options.userVerification,
    allowCredentials: options.allowCredentials?.map((credential) => ({
      id: base64UrlToArrayBuffer(credential.id),
      type: credential.type ?? 'public-key',
      transports: credential.transports,
    })),
  };
};

export const serializeAuthenticationCredential = (
  credential: PublicKeyCredential
): MFATypes.PasskeyAuthenticationCredential => {
  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    id: credential.id,
    rawId: arrayBufferToBase64Url(credential.rawId),
    type: credential.type as 'public-key',
    response: {
      clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
      authenticatorData: arrayBufferToBase64Url(response.authenticatorData),
      signature: arrayBufferToBase64Url(response.signature),
      userHandle: response.userHandle ? arrayBufferToBase64Url(response.userHandle) : undefined,
    },
  };
};

export const serializeRegistrationCredential = (
  credential: PublicKeyCredential
): MFATypes.PasskeyRegistrationCredential => {
  const response = credential.response as AuthenticatorAttestationResponse;
  return {
    id: credential.id,
    rawId: arrayBufferToBase64Url(credential.rawId),
    type: credential.type as 'public-key',
    response: {
      clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
      attestationObject: arrayBufferToBase64Url(response.attestationObject),
      clientExtensionResults: credential.getClientExtensionResults() as Record<string, unknown>,
    },
  };
};
